import { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch } from "shared/config/configureStore";
import { getProductsCategories } from "api/thunk/productCategories/getActions/getProductsCategories";
import { useAppSelector } from "shared/lib/hooks/hooks";
import { getMarathonsLookup } from "api/thunk/marathons/getActions/getMarathonsLookup";
import { createProduct } from "api/thunk/products/postActions/createProduct";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { getProductByProductId } from "api/thunk/products/getActions/getProductByProductId";
import { updateProduct } from "api/thunk/products/putActions/updateProduct";
import { useTranslation } from "react-i18next";

type FormValues = {
  id?: number;
  name: string;
  description: string;
  price: number | string;
  stock: number | string;
  categoryId: number | string;
  marathonId: number | string;
  images: FileList | null;
};

const fieldStyle = {
  width: "100%",
  height: 40,
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    "& fieldset": { borderColor: "#C0D4E3", borderWidth: 1 },
    "&:hover fieldset": { borderColor: "#0090D1" },
    "&.Mui-focused fieldset": { borderColor: "#0090D1" },
    "&.Mui-error fieldset": { borderColor: "#d32f2f" },
  },
};

const CreateProduct = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
    },
    mode: "onSubmit",
  });
  const images = watch("images");

  const { data: productCategories } = useAppSelector((state) => state.productsCategories.productsCategories.response);
  const { data: marathons } = useAppSelector((state) => state.marathons.marathonsLookup.response);
  const { data: productByProductId, loading: productByProductIdLoading } = useAppSelector((state) => state.products.productByProductId.response);
  const { loading: createProductLoading } = useAppSelector((state) => state.products.product);

  const [previews, setPreviews] = useState<string[]>([]);
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [existingUrls, setExistingUrls] = useState<string[]>([]);

  useEffect(() => {
    if (!images || images.length === 0) return;
    const urls = Array.from(images).map((file) => URL.createObjectURL(file));
    setPreviews(urls);
    return () => urls.forEach(URL.revokeObjectURL);
  }, [images]);

  const removeImage = (index: number) => {
    if (index < existingUrls.length) {
      setExistingUrls((prev) => prev.filter((_, i) => i !== index));
    } else {
      const newIndex = index - existingUrls.length;
      setNewFiles((prev) => prev.filter((_, i) => i !== newIndex));
    }

    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    dispatch(getProductsCategories({
      page: 1,
      pageSize: 10,
    }));
    dispatch(
      getMarathonsLookup({
        page: 1,
        pageSize: 10,
      })
    );
  }, [dispatch]);

  const clearForm = () => {
    reset({
      name: "",
      price: "",
      stock: "",
      description: "",
      marathonId: "",
      categoryId: "",
      images: null,
    });
    setNewFiles([]);
    setExistingUrls([]);
    setPreviews([]);
  };


  const onSubmit = async (data: FormValues) => {
    const formData = new FormData();

    if (id) formData.append("id", id);
    formData.append("name", data.name);
    formData.append("description", data.description);
    formData.append("price", String(data.price));
    formData.append("stock", String(data.stock));
    formData.append("categoryId", String(data.categoryId));
    formData.append("marathonId", String(data.marathonId));

    newFiles.forEach((file) => {
      formData.append("images", file);
    });

    for (const url of existingUrls) {
      const response = await fetch(url);
      const blob = await response.blob();
      const name = url.split("/").pop() || "image.jpg";
      const file = new File([blob], name, { type: blob.type });

      formData.append("images", file);
    }

    try {
      if (id) {
        await dispatch(updateProduct(formData as any)).unwrap();
        toast.success(String(t("toast.productUpdatedSuccessfully")));
      } else {
        await dispatch(createProduct(formData as any)).unwrap();
        toast.success(String(t("toast.productCreatedSuccessfully")));
      }

      clearForm();
      navigate("/admin_panel/marketplace?tab=products");
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getProductByProductId(Number(id)))
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && productByProductId) {
      reset({
        name: productByProductId.name || "",
        price: productByProductId.price || "",
        stock: productByProductId.stock || "",
        description: productByProductId.description || "",
        marathonId: productByProductId?.marathonId || "",
        categoryId: productByProductId?.categoryId || "",
        images: null,
      });

      if (productByProductId?.imagePaths?.length) {
        const urls = productByProductId.imagePaths.map((img: string) =>
          `https://marathon-app.timelysoft.org/${img.replaceAll("\\", "/")}`
        );
        setExistingUrls(urls);
        setPreviews(urls);
      } else {
        setExistingUrls([]);
        setPreviews([]);
      }
    } else if (!id) {
      clearForm();
    }
  }, [id, productByProductId, reset]);


  return (
    <Paper
      sx={{
        background: "#FFFFFF",
        borderRadius: "16px",
        p: 5,
        mt: 4,
        mx: "auto",
        maxWidth: 1140,
        width: "80%",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
      }}
    >
      {(createProductLoading || productByProductIdLoading) && (
        <Box
        >
          <CircularProgress
            size={28}
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              marginTop: "-12px",
              marginLeft: "-12px",
            }} />
        </Box>
      )}
      <Typography variant="h5" fontWeight={600} mb={4}>
        {id ? t("marketplace.updateProduct") : t("marketplace.createProduct")}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={3}>
          <Box>
            <InputLabel sx={{ mb: 1, mt: errors.name ? 2 : 1 }}>{t("marketplace.productName")}</InputLabel>
            <Controller
              name="name"
              control={control}
              rules={{ required: t("marketplace.productNameIsRequired") }}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    placeholder={t("marketplace.productName")}
                    sx={fieldStyle}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                </>
              )}
            />
          </Box>

          <Box>
            <InputLabel sx={{ mb: 1, mt: errors.price ? 2 : 1 }}>{t("marketplace.price")}</InputLabel>
            <Controller
              name="price"
              control={control}
              rules={{
                required: t("marketplace.productPriceIsRequired"),
                min: { value: 1, message: t("marketplace.priceGreaterThanZero") },
              }}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    placeholder={t("marketplace.price")}
                    type="number"
                    sx={fieldStyle}
                    error={!!errors.price}
                    helperText={errors.price?.message}
                  />
                </>
              )}
            />
          </Box>

          <Box>
            <InputLabel sx={{ mb: 1, mt: errors.categoryId ? 2 : 1 }}>{t("marketplace.category")}</InputLabel>
            <Controller
              name="categoryId"
              control={control}
              rules={{ required: t("marketplace.productCategoryIsRequired") }}
              render={({ field }) => (
                <FormControl sx={fieldStyle} error={!!errors.categoryId}>
                  <Select
                    {...field}
                    displayEmpty
                    value={field.value ?? ""}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "11px 14px",
                      }
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val === "" ? null : Number(val));
                    }}
                  >
                    <MenuItem value="" disabled>
                      <ListItemText primary={t("marketplace.category")} sx={{ color: "rgba(0, 0, 0, 0.3)" }} />
                    </MenuItem>
                    {!productCategories?.items.length && (
                      <MenuItem disabled>
                        {t("marketplace.noCategories")}
                      </MenuItem>
                    )}
                    {productCategories?.items?.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography color="error" fontSize={12} style={{ marginLeft: "16px" }}>
                    {errors.categoryId?.message}
                  </Typography>
                </FormControl>
              )}
            />

          </Box>

          <Box>
            <InputLabel sx={{ mb: 1, mt: errors.marathonId ? 2 : 1 }}>{t("marketplace.marathon")}</InputLabel>
            <Controller
              name="marathonId"
              control={control}
              rules={{ required: t("marketplace.productMarathonIsRequired") }}
              render={({ field }) => (
                <FormControl sx={fieldStyle} error={!!errors.marathonId}>
                  <Select
                    {...field}
                    displayEmpty
                    value={field.value ?? ""}
                    sx={{
                      "& .MuiSelect-select": {
                        padding: "11px 14px",
                      }
                    }}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(val === "" ? null : Number(val));
                    }}
                  >
                    <MenuItem value="" disabled>
                      <ListItemText primary={t("marketplace.marathon")} sx={{ color: "rgba(0, 0, 0, 0.3)" }} />
                    </MenuItem>
                    {!marathons?.items.length && (
                      <MenuItem disabled>
                        {t("marketplace.noMarathons")}
                      </MenuItem>
                    )}

                    {marathons?.items?.map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography color="error" fontSize={12} style={{ marginLeft: "16px" }}>
                    {errors.marathonId?.message}
                  </Typography>
                </FormControl>
              )}
            />
          </Box>

          <Box>
            <InputLabel sx={{ mb: 1, mt: errors.stock ? 2 : 1 }}>{t("marketplace.stock")}</InputLabel>
            <Controller
              name="stock"
              control={control}
              rules={{
                required: t("marketplace.productStockIsRequired"),
                min: { value: 0, message: t("marketplace.stockCannotBeNegative") },
              }}
              render={({ field }) => (
                <>
                  <TextField
                    {...field}
                    placeholder={t("marketplace.stock")}
                    type="number"
                    sx={fieldStyle}
                    error={!!errors.stock}
                    helperText={errors.stock && errors.stock.message}
                  />
                </>
              )}
            />
          </Box>
        </Box>

        <Box mt={4}>
          <InputLabel sx={{ mb: 1, mt: errors.description ? "50px" : 2 }}>{t("marketplace.description")}</InputLabel>
          <Controller
            name="description"
            control={control}
            rules={{
              required: t("marketplace.productDescriptionIsRequired"),
              minLength: { value: 10, message: t("marketplace.minimumCharacters") },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder={t("marketplace.description")}
                fullWidth
                multiline
                rows={4}
                sx={{
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "& fieldset": { borderColor: "#C0D4E3", borderWidth: 1 },
                    "&:hover fieldset": { borderColor: "#0090D1" },
                    "&.Mui-focused fieldset": { borderColor: "#0090D1" },
                    "&.Mui-error fieldset": { borderColor: "#d32f2f" },
                  },
                }}
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Box>

        <Box mt={4}>
          <Typography fontSize={14} mb={1}>
            {t("marketplace.productImages")}
          </Typography>

          <Controller
            name="images"
            control={control}
            rules={{
              validate: () =>
                newFiles.length + existingUrls.length > 0
                  ? true
                  : t("marketplace.atLeastOneImageIsRequired"),
            }}
            render={({ field }) => (
              <>
                <Button variant="outlined" component="label" sx={{ mb: 2, borderRadius: "12px" }}>
                  {t("marketplace.uploadImages")}
                  <input
                    hidden
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      if (!e.target.files) return;
                      const filesArray = Array.from(e.target.files);
                      setNewFiles((prev) => [...prev, ...filesArray])

                      const newPreviews = filesArray.map((file) => URL.createObjectURL(file));
                      setPreviews((prev) => [...prev, ...newPreviews]);
                    }}
                  />
                </Button>
                {errors.images && (
                  <Typography color="error" fontSize={12}>
                    {errors.images.message}
                  </Typography>
                )}
              </>
            )}
          />

          {previews.length > 0 && (
            <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
              {previews.map((src, index) => (
                <Box
                  key={index}
                  position="relative"
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "1px solid #E0E0E0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  <Box component="img" src={src} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      background: "#fff",
                      "&:hover": { background: "#f0f0f0" },
                    }}
                    onClick={() => removeImage(index)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={5}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              borderRadius: "12px",
              px: 5,
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {id ? t("marketplace.updateProduct") : t("marketplace.createProduct")}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default CreateProduct;
